import type { GeoPoint } from "../domain/location";
import { positionOverallLimitMs } from "../platform/PlatformServices";
import type {
  ClockSnapshot,
  CrewCache,
  LeaveAccountCache,
  LocalAttribution,
  LocalEvent,
  ProjectCache,
  ProjectPreferences,
  RoleSnapshot,
  RosterCache,
} from "./model";

/** Listener for changes of the local events. */
export type LocalEventsListener = (events: readonly LocalEvent[]) => void;

/**
 * Port for the local, device-only storage (outbox and offline state, spec 8.1, 11.4).
 */
export interface LocalStore {
  /**
   * Stores a new event. Rejected while the store is closed for a sign-out (F-57).
   *
   * @param event - The event.
   */
  addEvent(event: LocalEvent): Promise<void>;

  /**
   * Keeps an event the server has already accepted (`synced`, a copy of the record, for example
   * at an NFC tag). Skipped silently while the store is closed for a sign-out (F-57).
   *
   * @param event - The event.
   */
  addSyncedEvent(event: LocalEvent): Promise<void>;

  /**
   * Replaces stored events (status updates after synchronization).
   *
   * @param events - Updated events.
   */
  updateEvents(events: readonly LocalEvent[]): Promise<void>;

  /**
   * Adds the location captured after an own event was stored (T-175), only while the event waits
   * to be sent; an event already sent or rejected stays without it. Only the location is written
   * (and the wait for it ends, F-102), so a concurrent status update of the synchronization is
   * never undone.
   *
   * @param eventId - The event.
   * @param location - The rounded location.
   * @returns `true` if the location was added.
   */
  attachEventLocation(eventId: string, location: GeoPoint): Promise<boolean>;

  /**
   * Ends the wait of an event for its location without one (no permission, no result, elapsed
   * limit), so it is sent at once (F-102).
   *
   * @param eventId - The event.
   */
  releaseEventLocation(eventId: string): Promise<void>;

  /**
   * Returns all events of a person in chronological order (instant, then identifier).
   *
   * @param ownerSubject - OIDC subject.
   * @returns The events.
   */
  listEvents(ownerSubject: string): Promise<LocalEvent[]>;

  /**
   * Returns the events that can be submitted now, in chronological order: those before the first
   * event that still waits for its location (F-102). A wait ends with the location, without it, or
   * at the latest after {@link locationWaitMs}, so the location is not lost when a synchronization
   * runs during the request and an event is never held back for long; later events wait as well,
   * so the order of the steps stays.
   *
   * @param ownerSubject - OIDC subject.
   * @returns Pending events that can be sent.
   */
  listPending(ownerSubject: string): Promise<LocalEvent[]>;

  /**
   * Returns the owners on this device with clock events or project attributions waiting for
   * submission, of every person and tenant: signing out deletes them all (spec 11.4, F-84).
   *
   * @returns The owner keys, each once, in ascending order.
   */
  listPendingOwners(): Promise<string[]>;

  /**
   * Counts the clock events and project attributions of an owner waiting for submission through the
   * index, without reading the entries (data minimization, F-84).
   *
   * @param ownerSubject - Owner key.
   * @returns The number of unsent entries.
   */
  countPending(ownerSubject: string): Promise<number>;

  /**
   * Removes a rejected event after the conflict was handled (for example by a correction).
   *
   * @param id - Event identifier.
   */
  dismissRejected(id: string): Promise<void>;

  /**
   * Deletes confirmed events that were confirmed before a point in time.
   *
   * @param ownerSubject - OIDC subject.
   * @param syncedBeforeMs - Device time in milliseconds.
   * @returns Number of deleted events.
   */
  pruneSynced(ownerSubject: string, syncedBeforeMs: number): Promise<number>;

  /**
   * Returns the stored server snapshot.
   *
   * @param ownerSubject - OIDC subject.
   * @returns The snapshot or `null`.
   */
  getSnapshot(ownerSubject: string): Promise<ClockSnapshot | null>;

  /**
   * Stores the server snapshot.
   *
   * @param snapshot - The snapshot.
   */
  putSnapshot(snapshot: ClockSnapshot): Promise<void>;

  /**
   * Reads a device setting (for example the measured clock offset).
   *
   * @param key - The key.
   * @returns The value or `null`.
   */
  getSetting(key: string): Promise<string | null>;

  /**
   * Writes a device setting.
   *
   * @param key - The key.
   * @param value - The value.
   */
  putSetting(key: string, value: string): Promise<void>;

  /**
   * Observes the events of a person, including changes from other tabs.
   *
   * @param ownerSubject - OIDC subject.
   * @param listener - Called with the current events on every change.
   * @returns A function that ends the observation.
   */
  watchEvents(ownerSubject: string, listener: LocalEventsListener): () => void;

  /**
   * Observes the snapshot of a person.
   *
   * @param ownerSubject - OIDC subject.
   * @param listener - Called with the current snapshot on every change.
   * @returns A function that ends the observation.
   */
  watchSnapshot(
    ownerSubject: string,
    listener: (snapshot: ClockSnapshot | null) => void,
  ): () => void;

  /**
   * Stores a new project attribution (T-124).
   *
   * @param attribution - The attribution.
   */
  addAttribution(attribution: LocalAttribution): Promise<void>;

  /**
   * Replaces stored attributions (status updates after synchronization).
   *
   * @param attributions - Updated attributions.
   */
  updateAttributions(attributions: readonly LocalAttribution[]): Promise<void>;

  /**
   * Returns all attributions of a person in the order they were recorded.
   *
   * @param ownerSubject - Owner key.
   * @returns The attributions.
   */
  listAttributions(ownerSubject: string): Promise<LocalAttribution[]>;

  /**
   * Returns the attributions waiting for submission in the order they were recorded.
   *
   * @param ownerSubject - Owner key.
   * @returns Pending attributions.
   */
  listPendingAttributions(ownerSubject: string): Promise<LocalAttribution[]>;

  /**
   * Deletes confirmed attributions that were confirmed before a point in time.
   *
   * @param ownerSubject - Owner key.
   * @param syncedBeforeMs - Device time in milliseconds.
   * @returns Number of deleted attributions.
   */
  pruneSyncedAttributions(ownerSubject: string, syncedBeforeMs: number): Promise<number>;

  /**
   * Removes a rejected attribution after the person saw it.
   *
   * @param id - Attribution identifier.
   */
  dismissRejectedAttribution(id: string): Promise<void>;

  /**
   * Observes the attributions of a person.
   *
   * @param ownerSubject - Owner key.
   * @param listener - Called with the current attributions on every change.
   * @returns A function that ends the observation.
   */
  watchAttributions(
    ownerSubject: string,
    listener: (attributions: readonly LocalAttribution[]) => void,
  ): () => void;

  /**
   * Returns the cached projects of a person.
   *
   * @param ownerSubject - Owner key.
   * @returns The cache or `null`.
   */
  getProjectCache(ownerSubject: string): Promise<ProjectCache | null>;

  /**
   * Stores the projects of a person.
   *
   * @param cache - The cache.
   */
  putProjectCache(cache: ProjectCache): Promise<void>;

  /**
   * Observes the cached projects of a person.
   *
   * @param ownerSubject - Owner key.
   * @param listener - Called with the current cache on every change.
   * @returns A function that ends the observation.
   */
  watchProjectCache(
    ownerSubject: string,
    listener: (cache: ProjectCache | null) => void,
  ): () => void;

  /**
   * Returns favorites and recently used projects of a person.
   *
   * @param ownerSubject - Owner key.
   * @returns The preferences (empty lists if none were stored).
   */
  getProjectPreferences(ownerSubject: string): Promise<ProjectPreferences>;

  /**
   * Stores favorites and recently used projects.
   *
   * @param preferences - The preferences.
   */
  putProjectPreferences(preferences: ProjectPreferences): Promise<void>;

  /**
   * Observes favorites and recently used projects of a person.
   *
   * @param ownerSubject - Owner key.
   * @param listener - Called with the current preferences on every change.
   * @returns A function that ends the observation.
   */
  watchProjectPreferences(
    ownerSubject: string,
    listener: (preferences: ProjectPreferences) => void,
  ): () => void;

  /**
   * Returns the last known roles of a person (F-05).
   *
   * @param ownerSubject - Owner key (subject and chosen tenant).
   * @returns The snapshot or `null`.
   */
  getRoleSnapshot(ownerSubject: string): Promise<RoleSnapshot | null>;

  /**
   * Stores the last known roles of a person and deletes the snapshots of every other account, so
   * that the device keeps the roles of the signed-in account only.
   *
   * @param snapshot - The snapshot.
   */
  putRoleSnapshot(snapshot: RoleSnapshot): Promise<void>;

  /**
   * Returns whether this device keeps the roles of a person from a settled answer of `GET /me` for
   * any tenant (F-84): the person was seen in the application of the tenants here.
   *
   * @param subject - OIDC subject.
   * @returns `true` if a role snapshot of the person is stored.
   */
  hasRoleSnapshot(subject: string): Promise<boolean>;

  /**
   * Deletes the role snapshots of every account other than the given one (the account changed).
   *
   * @param subject - OIDC subject of the signed-in account.
   */
  retainRoleSnapshots(subject: string): Promise<void>;

  /**
   * Returns the last known leave account of a person for a leave year (T-163).
   *
   * @param ownerSubject - Owner key.
   * @param leaveYear - The leave year.
   * @returns The cache or `null`.
   */
  getLeaveAccount(ownerSubject: string, leaveYear: number): Promise<LeaveAccountCache | null>;

  /**
   * Stores the leave account of a person for a leave year, replacing the earlier one, and deletes the
   * leave accounts of every other account.
   *
   * @param cache - The cache.
   */
  putLeaveAccount(cache: LeaveAccountCache): Promise<void>;

  /**
   * Deletes the leave accounts of every account other than the given one (the account changed).
   *
   * @param subject - OIDC subject of the signed-in account.
   */
  retainLeaveAccounts(subject: string): Promise<void>;

  /**
   * Returns the last known crew of a person (T-170).
   *
   * @param ownerSubject - Owner key.
   * @returns The cache or `null`.
   */
  getCrewCache(ownerSubject: string): Promise<CrewCache | null>;

  /**
   * Stores the crew of a person and deletes the crews of other accounts.
   *
   * @param cache - The crew.
   */
  putCrewCache(cache: CrewCache): Promise<void>;

  /**
   * Deletes the crew of a person, for example when the last known roles no longer allow clocking for
   * others (T-170).
   *
   * @param ownerSubject - Owner key.
   */
  deleteCrewCache(ownerSubject: string): Promise<void>;

  /**
   * Deletes the crews of every account other than the signed-in one.
   *
   * @param subject - OIDC subject of the signed-in account.
   */
  retainCrewCaches(subject: string): Promise<void>;

  /**
   * Returns the last known „Mein Dienstplan“ of a person (T-178).
   *
   * @param ownerSubject - Owner key.
   * @returns The cache or `null`.
   */
  getRosterCache(ownerSubject: string): Promise<RosterCache | null>;

  /**
   * Stores „Mein Dienstplan“ of a person and deletes the rosters of other accounts.
   *
   * @param cache - The roster.
   */
  putRosterCache(cache: RosterCache): Promise<void>;

  /**
   * Deletes the roster of a person, for example once rostering is switched off.
   *
   * @param ownerSubject - Owner key.
   */
  deleteRosterCache(ownerSubject: string): Promise<void>;

  /**
   * Deletes the rosters of every account other than the signed-in one.
   *
   * @param subject - OIDC subject of the signed-in account.
   */
  retainRosterCaches(subject: string): Promise<void>;

  /**
   * Refuses every write from now on, at the beginning of a sign-out: nothing is acknowledged as
   * stored while the sign-out runs (F-57). Caches and states are skipped silently; adding an event
   * or an attribution is rejected.
   */
  close(): void;

  /**
   * Deletes all local data (sign-out, spec 11.4) and keeps the store closed (see {@link close})
   * until {@link reopen}: a cache or a synchronization still running during the sign-out (a
   * profile, the clock snapshot, the roster) must not store the person's data again. If the
   * deletion fails, the store is open again and the error is passed on (F-57).
   */
  clearAll(): Promise<void>;

  /**
   * Accepts writes again: after a sign-in, or when a sign-out failed and the person is still
   * signed in (F-57).
   */
  reopen(): void;
}

/** Keys of device settings. */
export const settingKeys = {
  clockOffsetMs: "clockOffsetMs",
  lastSyncAtMs: "lastSyncAtMs",
  deviceId: "deviceId",
  backgroundSyncCredential: "backgroundSyncCredential",
  /** Device token of the native app registered for push notifications; empty if none (T-081). */
  nativePushToken: "nativePushToken",
} as const;

/**
 * Longest wait of an event for its location before it is sent (F-102): the overall limit of the
 * position request, permission dialog included (T-175).
 */
export const locationWaitMs = positionOverallLimitMs;

/**
 * Whether an event still waits for its location. A wait further ahead than {@link locationWaitMs}
 * (the device clock was set back) counts as ended, so a step is never held back for long.
 *
 * @param event - The event.
 * @param nowMs - Device time in milliseconds.
 * @returns `true` while the event waits.
 */
export function isAwaitingLocation(event: LocalEvent, nowMs: number): boolean {
  const until = event.locationAwaitedUntilMs ?? null;
  const awaiting = until !== null && until > nowMs && until - nowMs <= locationWaitMs;

  return awaiting;
}

/**
 * The pending events that can be sent now: those before the first event that waits for its
 * location, so the order of the steps stays (F-102).
 *
 * @param pending - Pending events in chronological order.
 * @param nowMs - Device time in milliseconds.
 * @returns The events to send.
 */
export function sendableNow(pending: readonly LocalEvent[], nowMs: number): LocalEvent[] {
  const firstWaiting = pending.findIndex((event) => isAwaitingLocation(event, nowMs));
  const sendable = firstWaiting < 0 ? [...pending] : pending.slice(0, firstWaiting);

  return sendable;
}
